<script setup>
import { ref, computed, watch } from 'vue'
import "firebase/functions";
import firebase from "firebase/app";
import Shell from './Shell.vue'

const props = defineProps({
  modelValue: {
    default: null,
  },
  label: String,
  tooltip: String,
  placeholder: {
    type: String,
    default: 'Search aircraft...',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])

const selected = ref(props.modelValue)
watch(() => props.modelValue, (val) => { selected.value = val })

const searchQuery = ref('')
const results = ref([])
const loading = ref(false)
const hasSearched = ref(false)
const isOpen = ref(false)

let debounceTimer = null

const displayValue = computed(() => {
  if (props.modelValue) {
    return `${props.modelValue.manufacturer} | ${props.modelValue.model} | ${props.modelValue.variant}`
  }
  return ''
})

function onInputChange(event) {
  const value = event.target.value
  searchQuery.value = value

  if (debounceTimer) clearTimeout(debounceTimer)

  if (!value || value.trim().length < 2) {
    results.value = []
    hasSearched.value = false
    loading.value = false
    isOpen.value = false
    return
  }

  loading.value = true
  isOpen.value = true

  debounceTimer = setTimeout(async () => {
    try {
      const searchAircraft = firebase.app().functions().httpsCallable('searchAircraft')
      const result = await searchAircraft({ query: value.trim() })
      results.value = result.data.results || []
      hasSearched.value = true
    } catch (err) {
      console.error('Aircraft search failed:', err)
      results.value = []
    } finally {
      loading.value = false
    }
  }, 300)
}

function onSelect(result) {
  const plain = { ...result }
  selected.value = plain
  emit('update:modelValue', plain)
  searchQuery.value = ''
  results.value = []
  hasSearched.value = false
  isOpen.value = false
}

function clear() {
  selected.value = null
  emit('update:modelValue', null)
  searchQuery.value = ''
  results.value = []
  hasSearched.value = false
  isOpen.value = false
}

function onBlur() {
  setTimeout(() => {
    isOpen.value = false
  }, 150)
}
</script>

<template>
  <Shell :label="label" :tooltip="tooltip">
    <template #comp>
      <div class="relative w-full">

        <!-- Selected value display -->
        <div v-if="selected" class="relative flex items-center">
          <font-awesome-icon icon="check" class="absolute left-0 text-green" />
          <span class="text-sm pl-5 pr-6 truncate" style="line-height: 36px">
           {{ selected.manufacturer }} | {{ selected.variant }}
          </span>
          <button
            type="button"
            class="absolute right-0 text-gray hover:text-primary focus:outline-none"
            @click.stop="clear"
            :disabled="disabled"
          >
            <font-awesome-icon icon="times" />
          </button>
        </div>

        <!-- Search input -->
        <div v-else class="relative flex items-center">
          <font-awesome-icon v-if="loading" icon="spinner" spin class="absolute left-0 text-primary" />
          <font-awesome-icon v-else icon="search" class="absolute left-0 text-gray" />
          <input class="border-b-2 border-gray focus:border-primary focus:border-b-2 w-full text-sm pl-5"
            style="line-height: 36px; background: rgba(0, 0, 0, 0); outline: none" :placeholder="placeholder"
            :disabled="disabled" :value="searchQuery" @input="onInputChange" @blur="onBlur" autocomplete="off" />
        </div>

        <!-- Results dropdown -->
        <transition leave-active-class="transition duration-100 ease-in" leave-from-class="opacity-100"
          leave-to-class="opacity-0">
          <div v-if="!selected && isOpen"
            class="absolute z-10 w-full rounded  m2-1 rounded bg-gradient-to-br from-gray-50 to-gray-100 shadow-inner overflow-auto">
            <!-- Loading state -->
            <div v-if="loading" class="px-4 py-2 text-gray">
              <font-awesome-icon icon="spinner" spin class="mr-2" />
              Searching…
            </div>

            <!-- No results -->
            <div v-else-if="hasSearched && results.length === 0" class="px-4 py-2 text-gray italic">
              No aircraft found.
            </div>

            <template v-else-if="results.length > 0">
              <!-- Header row -->
              <div
                class="sticky top-0 grid grid-cols-3 gap-2 border-b border-gray-200 bg-gray-100 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray">
                <span>Manufacturer</span>
                <span>Variant</span>
                <span>Model</span>
              </div>

              <!-- Result items -->
              <div class="max-h-60  py-1 ">
                <div v-for="result in results" :key="result._id"
                  class="cursor-pointer select-none px-4 py-2 hover:bg-primary-50 hover:text-primary"
                  @mousedown.prevent="onSelect(result)">
                  <div class="grid grid-cols-3 gap-2 items-center text-xs">
                    <span class="truncate">{{ result.manufacturer }}</span>
                    <span class="truncate">{{ result.variant }}</span>
                    <span class="truncate">{{ result.model }}</span>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </transition>
      </div>
    </template>
  </Shell>
</template>
